/**
 * MemberList Component
 * Displays all organization members with search and filtering
 */
import { useState, useMemo } from 'react';
import { MemberWithQuota } from '../../api/organizations';
import MemberCard from './MemberCard';
import { MagnifyingGlassIcon } from './icons';

interface MemberListProps {
  members: MemberWithQuota[];
  currentUserId: string;
  ownerId: string;
  loading?: boolean;
  onEditQuota: (member: MemberWithQuota) => void;
  onRemoveMember: (member: MemberWithQuota) => void;
  onRefresh?: () => void;
}

export default function MemberList({
  members,
  currentUserId,
  ownerId,
  loading = false,
  onEditQuota,
  onRemoveMember,
  onRefresh,
}: MemberListProps) {
  const [searchQuery, setSearchQuery] = useState('');

  // Filter and sort members
  const filteredMembers = useMemo(() => {
    let filtered = [...members];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (m) =>
          m.member.full_name?.toLowerCase().includes(query) ||
          m.member.preferred_name?.toLowerCase().includes(query) ||
          m.member.email.toLowerCase().includes(query)
      );
    }

    // Sort: owner first, then admin, then by join date
    filtered.sort((a, b) => {
      // Owner always first
      if (a.member.id === ownerId) return -1;
      if (b.member.id === ownerId) return 1;

      // Then admins
      const aIsAdmin = a.member.user_role === 'org_admin';
      const bIsAdmin = b.member.user_role === 'org_admin';
      if (aIsAdmin && !bIsAdmin) return -1;
      if (!aIsAdmin && bIsAdmin) return 1;

      // Then by join date (newest first)
      return new Date(b.member.created_at).getTime() - new Date(a.member.created_at).getTime();
    });

    return filtered;
  }, [members, searchQuery, ownerId]);

  // Loading skeleton
  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="bg-dark-card/50 border border-dark-border/50 rounded-lg p-5 h-48 animate-pulse"
          />
        ))}
      </div>
    );
  }

  // Empty state
  if (members.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="w-16 h-16 mb-4 flex items-center justify-center text-dark-text-muted">
          <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-dark-text mb-2">No Members Yet</h3>
        <p className="text-sm text-dark-text-muted text-center max-w-sm mb-6">
          Your organization has no subusers. Invite members to get started.
        </p>
      </div>
    );
  }

  // No search results
  if (filteredMembers.length === 0) {
    return (
      <div className="space-y-4">
        {/* Search bar */}
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-dark-text-muted" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search members by name or email..."
            className="w-full bg-dark-bg/50 border border-dark-border/50 rounded-xl pl-12 pr-4 py-3
                     text-dark-text placeholder-gray-500
                     focus:outline-none focus:ring-2 focus:ring-accent-blue/50 focus:border-accent-blue
                     transition-all"
          />
        </div>

        {/* No results */}
        <div className="flex flex-col items-center justify-center py-12">
          <p className="text-sm text-dark-text-muted">
            No members match "{searchQuery}"
          </p>
          <button
            onClick={() => setSearchQuery('')}
            className="mt-4 px-4 py-2 text-sm text-accent-blue hover:underline"
          >
            Clear search
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with search */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="text-sm text-dark-text-muted">
          {filteredMembers.length} {filteredMembers.length === 1 ? 'member' : 'members'}
          {searchQuery && ` matching "${searchQuery}"`}
        </div>

        <div className="relative flex-1 max-w-md">
          <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-dark-text-muted" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search members..."
            className="w-full bg-dark-bg/50 border border-dark-border/50 rounded-xl pl-12 pr-4 py-2.5
                     text-sm text-dark-text placeholder-gray-500
                     focus:outline-none focus:ring-2 focus:ring-accent-blue/50 focus:border-accent-blue
                     transition-all"
          />
        </div>

        {onRefresh && (
          <button
            onClick={onRefresh}
            className="p-2.5 text-dark-text-muted hover:text-dark-text
                     bg-dark-bg/50 border border-dark-border/50 rounded-lg
                     hover:bg-dark-bg hover:border-accent-blue/50
                     transition-all"
            title="Refresh members"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Member cards */}
      <div className="space-y-4">
        {filteredMembers.map((member) => (
          <MemberCard
            key={member.member.id}
            member={member}
            isCurrentUser={member.member.id === currentUserId}
            isOwner={member.member.id === ownerId}
            isAdmin={member.member.user_role === 'org_admin'}
            onEditQuota={onEditQuota}
            onRemove={onRemoveMember}
          />
        ))}
      </div>
    </div>
  );
}
